// The left rail's layout: which of the things it can hold show, and in what
// order. Kept in this browser, like the camera and the chat settings, so each
// person's rail is their own. Select and Add a widget always stay.

import { create } from "zustand";
import type { WidgetType } from "../../shared/types";
import { readStoredJson, removeStored, writeStoredJson } from "../lib/stored";

export type RailGroup = "tools" | "add" | "board";

export const RAIL_GROUPS: RailGroup[] = ["tools", "add", "board"];

/** Widgets the rail can add in one click: the ones useful the moment they land, with nothing to connect (the weather asks for its place in the tile). */
export const QUICK_ADDS = ["sticky", "timer", "clock", "tasks", "notes", "calendar", "countdown", "habits", "links", "heading", "weather"] as const satisfies readonly WidgetType[];

export type QuickAdd = (typeof QUICK_ADDS)[number];

export type RailId = "select" | "draw" | "gallery" | `add:${QuickAdd}` | "data" | "boards" | "look" | "fit" | "fullscreen" | "shortcuts";

export interface RailSlot {
  id: RailId;
  on: boolean;
}

interface RailDef {
  id: RailId;
  group: RailGroup;
  /** shows out of the box */
  on: boolean;
  /** always shows; it can move but not go */
  fixed?: boolean;
}

/** Everything the rail can hold, in the order it comes. Out of the box it is calmer than it can be. */
export const RAIL_DEFS: RailDef[] = [
  { id: "select", group: "tools", on: true, fixed: true },
  { id: "draw", group: "tools", on: true },
  { id: "gallery", group: "add", on: true, fixed: true },
  ...QUICK_ADDS.map((t): RailDef => ({ id: `add:${t}`, group: "add", on: t === "sticky" || t === "timer" })),
  { id: "data", group: "board", on: true },
  { id: "boards", group: "board", on: false },
  { id: "look", group: "board", on: false },
  { id: "fit", group: "board", on: false },
  { id: "fullscreen", group: "board", on: false },
  { id: "shortcuts", group: "board", on: false },
];

const DEF = new Map(RAIL_DEFS.map((d) => [d.id, d]));

export const groupOf = (id: RailId): RailGroup => DEF.get(id)!.group;
export const isFixed = (id: RailId): boolean => !!DEF.get(id)?.fixed;

export function defaultRail(): RailSlot[] {
  return RAIL_DEFS.map((d) => ({ id: d.id, on: d.on }));
}

/**
 * A saved layout made safe to draw: unknown and repeated entries go, the
 * fixed ones are on whatever was saved, and anything added to the rail since
 * comes in at the end of its group, on or off as it ships.
 */
export function normalizeRail(saved: unknown): RailSlot[] {
  const list = saved && typeof saved === "object" && Array.isArray((saved as { slots?: unknown }).slots) ? ((saved as { slots: unknown[] }).slots) : null;
  if (!list) return defaultRail();
  const out: RailSlot[] = [];
  const seen = new Set<string>();
  for (const s of list) {
    const id = (s as { id?: unknown } | null)?.id;
    if (typeof id !== "string" || seen.has(id) || !DEF.has(id as RailId)) continue;
    seen.add(id);
    const on = (s as { on?: unknown }).on;
    out.push({ id: id as RailId, on: isFixed(id as RailId) || (typeof on === "boolean" ? on : DEF.get(id as RailId)!.on) });
  }
  for (const d of RAIL_DEFS) if (!seen.has(d.id)) out.push({ id: d.id, on: d.on });
  return out;
}

/** Moves one entry to a place among the others of its group (0 is the top). */
export function moveSlot(slots: RailSlot[], id: RailId, to: number): RailSlot[] {
  const group = groupOf(id);
  const peers = slots.filter((s) => groupOf(s.id) === group);
  const from = peers.findIndex((s) => s.id === id);
  const at = Math.max(0, Math.min(peers.length - 1, to));
  if (from < 0 || from === at) return slots;
  const moved = [...peers];
  const [s] = moved.splice(from, 1);
  moved.splice(at, 0, s);
  // the group keeps the places it had in the list; only its own order changes
  let i = 0;
  return slots.map((x) => (groupOf(x.id) === group ? moved[i++] : x));
}

export function sameRail(a: RailSlot[], b: RailSlot[]): boolean {
  return a.length === b.length && a.every((s, i) => s.id === b[i].id && s.on === b[i].on);
}

// ---------------------------------------------------------------------------
// kept in this browser

const KEY = "cb:rail";

function load(): RailSlot[] {
  return normalizeRail(readStoredJson<unknown>(KEY, null));
}

function save(slots: RailSlot[]) {
  // private mode: it lasts as long as the tab
  if (sameRail(slots, defaultRail())) removeStored(KEY);
  else writeStoredJson(KEY, { v: 1, slots });
}

export const useRail = create<{ slots: RailSlot[]; editing: boolean }>(() => ({ slots: load(), editing: false }));

function put(slots: RailSlot[]) {
  useRail.setState({ slots });
  save(slots);
}

export function showOnRail(id: RailId, on: boolean) {
  if (isFixed(id)) return;
  put(useRail.getState().slots.map((s) => (s.id === id ? { ...s, on } : s)));
}

export function moveOnRail(id: RailId, to: number) {
  put(moveSlot(useRail.getState().slots, id, to));
}

export function resetRail() {
  put(defaultRail());
}

export function openRailEditor(open = true) {
  useRail.setState({ editing: open });
}

// another tab changed it
if (typeof window !== "undefined")
  window.addEventListener("storage", (e) => {
    if (e.key === KEY || e.key === null) useRail.setState({ slots: load() });
  });
